import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import {
  SparePartType,
  useAddSparePartToArticleAction,
  useGetSparePartTypesForAction,
} from '@/models/articleActionSparePart';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

import { SparePartDropdownItem } from './SparePartDropdownItem';

export const SparePartSearchSelect = ({
  actionId,
  onBlur,
}: {
  actionId: string;
  onBlur: () => void;
}) => {
  const { t } = useLingui();
  const { article, request } = useArticleContext();
  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);
  const { data: sparePartTypes, isFetching } = useGetSparePartTypesForAction(
    {
      articleId: article.id,
      actionId,
      query: debouncedQuery || undefined,
    },
    {
      keepPreviousData: true,
    }
  );
  const { mutate: addSparePart } = useAddSparePartToArticleAction({
    articleId: article.id,
    requestId: request.id,
    actionId,
  });

  const handleSelectionChange = (sparePartType: SparePartType) => {
    if (!sparePartType) return;

    addSparePart({ sparePartType });
    setQuery('');
    onBlur();
  };

  return (
    <InputSelect
      aria-label={t({
        id: 'spare-part-type-search.add-spare-part.label',
        message: 'Add a spare part',
      })}
      placeholder={t({
        id: 'spare-part-type-search.add-spare-part.placeholder',
        message: 'Search a spare part...',
      })}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      menuIsOpen
      inputValue={query}
      onInputChange={setQuery}
      filterOption={null}
      isLoading={isFetching || query !== debouncedQuery}
      getOptionValue={(sparePartType) => sparePartType.id}
      formatOptionLabel={(sparePartType) => <SparePartDropdownItem sparePartType={sparePartType} />}
      onBlur={onBlur}
      onChange={(sparePartType) => {
        if (sparePartType) {
          handleSelectionChange(sparePartType);
        }
      }}
      options={sparePartTypes}
      variant="add"
    />
  );
};
